import { Box, Text, VStack } from '@chakra-ui/react';
import { QRCode } from 'react-qr-code';
import { IS_PROD } from '../../config/env';
import { ROUTES } from '../../constants/routes';
import { safeAnalytics } from '../../utils/analytics';
import { ANALYTICS_EVENTS } from '../../constants/analytics';

interface MobileAccessQRProps {
  /** QR pixel size. Default 104 — fits a sidebar menu without dominating it. */
  size?: number;
  /**
   * App-relative path (query string included) to encode instead of the bare
   * capture screen — e.g. the equipment handoff, which carries `mode`/`intent`
   * so the phone lands in the right capture flow. Must start with '/'.
   */
  path?: string;
  /** Caption under the QR. Defaults to the generic "open Bower" wording. */
  caption?: string;
}

/** The only query params a Bower access QR may carry. */
const ALLOWED_QR_PARAMS = new Set(['mode', 'intent', 'src', 'ws']);

/**
 * Enforce the credential-free contract structurally rather than by docblock.
 *
 * `path` accepts a caller-built query string, so this component is one careless
 * caller away from becoming the standing bearer-credential QR its docblock
 * exists to prevent. Falls back to the plain capture path (always safe) and
 * reports, rather than throwing mid-render and blanking the surface.
 */
function safeQrPath(path: string | undefined): string {
  if (!path) return ROUTES.mobileCapture;

  const reject = (reason: string): string => {
    // A denylist here would be endless — token, customToken, idToken, jwt,
    // refreshToken, code, sig... so this is an ALLOWLIST: anything not on the
    // short known-good list is refused, credential-shaped or not.
    console.error('[MobileAccessQR] Refusing to encode an unsafe path.', { reason });
    safeAnalytics.track(ANALYTICS_EVENTS.QR_PATH_REJECTED, { reason });
    return ROUTES.mobileCapture;
  };

  // Same rejection set as safeInternalReturnUrl: a QR is followed by a camera,
  // not read by a human, so an off-origin destination has no visible tell.
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) {
    return reject('off_origin');
  }
  // A fragment never carries anything this QR needs, and is a place a
  // credential can hide from a params-only check.
  if (path.includes('#')) return reject('fragment');

  const [, query] = path.split('?');
  if (query) {
    for (const key of new URLSearchParams(query).keys()) {
      if (!ALLOWED_QR_PARAMS.has(key)) return reject('disallowed_param');
    }
  }
  return path;
}

/**
 * The absolute URL a phone should open, for `path`. Exported so a text
 * alternative (copy-link) resolves to the SAME URL the QR encodes — prod
 * serves the app at app.bowerlabs.ai, so deriving the base independently at a
 * call site would hand out a different link than the one being scanned.
 */
export function buildMobileAccessUrl(path?: string): string {
  // Prod serves the app at app.bowerlabs.ai; dev/staging use the current
  // origin. Mirrors the base-URL convention in services/auth.service.ts.
  const base = IS_PROD ? 'https://app.bowerlabs.ai' : window.location.origin;
  return `${base}${safeQrPath(path)}`;
}

/**
 * A static QR code to open Bower on a phone.
 *
 * It encodes the plain mobile URL — deliberately NOT a magic link. A magic-link
 * QR redeems into a full Firebase session (`createCustomToken`), so it's a
 * bearer credential: anyone who scans or screenshots it within its TTL is
 * signed in as the user. Since the Create menu is always available, embedding a
 * standing magic-link QR there would be an easy auth-bypass. This QR carries no
 * credential — scanning just opens the app and the user signs in normally.
 *
 * Presentational + self-contained, so it can be reused for any "use mobile"
 * affordance.
 */
export function MobileAccessQR({
  size = 104,
  path,
  caption = 'Scan to open Bower on your phone',
}: MobileAccessQRProps) {
  const url = buildMobileAccessUrl(path);

  return (
    <VStack spacing={2} align="center" py={1}>
      {/* bg="white" is a Chakra colour alias (always #FFFFFF, doesn't flip in
          dark mode) — correct here: a QR needs a white quiet zone to scan.
          role=img + aria-label give the otherwise-unlabelled <svg> an
          accessible name (react-qr-code doesn't forward aria onto the svg). */}
      <Box
        role="img"
        // Derived from the caption so the accessible name always matches the
        // visible label, and so two QRs on one screen don't collide on name.
        aria-label={`QR code: ${caption}`}
        bg="white"
        p={2}
        borderRadius="md"
        border="1px"
        borderColor="border.default"
      >
        <QRCode value={url} size={size} />
      </Box>
      <Text fontSize="xs" color="text.muted" textAlign="center" maxW="200px">
        {caption}
      </Text>
    </VStack>
  );
}
